import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Divider,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { TextField, Button } from '../../components/primitives';
import { FacilityForm } from '../../components/domain/facility';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Step 1: Create Facility
 * Searchable dropdown for selecting an existing facility
 */
const CreateFacility = ({
  onFacilitySelect,
  selectedFacility: propSelectedFacility,
  onFooterChange,
}) => {
  const [facilities, setFacilities] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedFacility, setSelectedFacility] = useState(propSelectedFacility || null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAddFacility, setOpenAddFacility] = useState(false);
  const containerRef = useRef(null);

  // Format facility display name
  const formatFacilityDisplay = (facility) => {
    const name = facility.name || 'Unnamed Facility';
    const city = facility.address?.city || '';
    const state = facility.address?.state || '';
    
    if (city && state) {
      return `${name} (${city}, ${state})`;
    } else if (city) {
      return `${name} (${city})`;
    } else if (state) {
      return `${name} (${state})`;
    }
    return name;
  };

  // Sync with prop when it changes from parent
  useEffect(() => {
    if (propSelectedFacility) {
      setSelectedFacility(propSelectedFacility);
      setSearchText(formatFacilityDisplay(propSelectedFacility));
    } else if (propSelectedFacility === null && selectedFacility !== null) {
      // Clear selection if prop is explicitly set to null
      setSelectedFacility(null);
      setSearchText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propSelectedFacility]);

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/facilities/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setFacilities(data);
    } catch (err) {
      console.error('Error fetching facilities:', err);
      setError('Failed to load facilities. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter facilities based on search text
  const filteredFacilities = useMemo(() => {
    if (!searchText.trim()) {
      return facilities;
    }

    const searchLower = searchText.toLowerCase();
    return facilities.filter((facility) => {
      const nameMatch = facility.name?.toLowerCase().includes(searchLower);
      const cityMatch = facility.address?.city?.toLowerCase().includes(searchLower);
      const stateMatch = facility.address?.state?.toLowerCase().includes(searchLower);
      return nameMatch || cityMatch || stateMatch;
    });
  }, [facilities, searchText]);

  // Get display value for input field
  const inputValue = selectedFacility ? formatFacilityDisplay(selectedFacility) : searchText;

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setSelectedFacility(null); // Clear selection when user types
  };

  const handleInputFocus = () => {
    // If a facility is selected, clear it and allow new search
    if (selectedFacility) {
      setSelectedFacility(null);
      setSearchText('');
    }
    setShowDropdown(true);
  };

  const handleFacilitySelect = (facility) => {
    setSelectedFacility(facility);
    setSearchText(formatFacilityDisplay(facility));
    setShowDropdown(false); // Hide dropdown after selection
    // Pass selection to parent component
    if (onFacilitySelect) {
      onFacilitySelect(facility);
    }
  };

  const handleFacilitySaved = useCallback((newFacility) => {
    setFacilities((prev) => [newFacility, ...prev]);
    setSelectedFacility(newFacility);
    setSearchText(formatFacilityDisplay(newFacility));
    setShowDropdown(false);
    if (onFacilitySelect) {
      onFacilitySelect(newFacility);
    }
    setOpenAddFacility(false);
  }, [onFacilitySelect]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!onFooterChange) return undefined;

    if (loading || error) {
      onFooterChange(null);
      return () => onFooterChange(null);
    }

    onFooterChange(
      <>
        <Typography
          variant="body2"
          sx={{
            mb: 2,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          Can't find what you're looking for?
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button variant="outlined" onClick={() => setOpenAddFacility(true)}>
            Add Facility
          </Button>
        </Box>

        <Dialog
          open={openAddFacility}
          onClose={() => setOpenAddFacility(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent sx={{ pt: 3 }}>
            <FacilityForm
              onSaved={handleFacilitySaved}
              showActions={true}
              submitLabel="Submit"
            />
          </DialogContent>
        </Dialog>
      </>
    );

    return () => onFooterChange(null);
  }, [loading, error, openAddFacility, onFooterChange, handleFacilitySaved]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }} ref={containerRef}>
      {/* Title */}
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          mb: 2,
          color: 'text.primary',
        }}
      >
        Choose from existing facilities:
      </Typography>

      {/* Search Input */}
      <TextField
        fullWidth
        placeholder="Start typing facility name, city, or state..."
        value={inputValue}
        onChange={handleSearchChange}
        onFocus={handleInputFocus}
        sx={{
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: 1,
          },
        }}
      />

      {/* Dropdown List */}
      {showDropdown && filteredFacilities.length > 0 ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 400,
            overflowY: 'auto',
            backgroundColor: 'background.paper',
          }}
        >
          <List disablePadding>
            {filteredFacilities.map((facility, index) => (
              <React.Fragment key={facility.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleFacilitySelect(facility)}
                    sx={{
                      py: 1.5,
                      px: 2,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.primary',
                        fontWeight: 400,
                      }}
                    >
                      {formatFacilityDisplay(facility)}
                    </Typography>
                  </ListItemButton>
                </ListItem>
                {index < filteredFacilities.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      ) : showDropdown && searchText.trim() ? (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No facilities found matching your search.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default CreateFacility;
